import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { userManager } from '../lib/userManager';
import { clinicManager } from '../lib/clinicManager';
import { permissionManager } from '../lib/permissions';
import { topicManager } from '../lib/topicManager';
import { multiClinicUserManager } from '../lib/multiClinicUserManager';
import { inviteAPI } from '../lib/inviteAPI';
import { quotaManager } from '../lib/quotaManager';
import { patientRequestManager } from '../lib/patientRequestManager';
import QuotaDisplay from './QuotaDisplay';
import PatientRequestForm from './PatientRequestForm';
import { GENDER_OPTIONS, RELATION_OPTIONS, getTreatmentTypeById } from '../utils/appSettings';
import { RefreshIcon, PenIcon, EyeIcon } from './icons/Icons';
// import { multiClinicUserManager } from '../lib/multiClinicUserManager'; // Unused in current implementation
import './AdminPanel.css';

/**
 * Unified Loading Indicator Component
 * Same style as used for S3 HTML loading
 */
const LoadingIndicator = ({
  message = "Loading...",
  subMessage = "Please wait while we fetch the data",
  emoji = "🔄",
  minHeight = "300px"
}) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: minHeight,
      textAlign: 'center',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
      padding: '40px',
      borderRadius: '8px',
      margin: '20px 0'
    }}>
      <div style={{
        fontSize: '48px',
        marginBottom: '20px',
        animation: 'pulse 2s infinite'
      }}>
        {emoji}
      </div>
      <h3 style={{
        color: '#495057',
        fontSize: '24px',
        marginBottom: '12px',
        fontWeight: '600'
      }}>
        {message}
      </h3>
      <p style={{
        color: '#6c757d',
        fontSize: '16px',
        margin: '0',
        fontWeight: '400'
      }}>
        {subMessage}
      </p>
      <div style={{
        marginTop: '24px',
        width: '200px',
        height: '4px',
        backgroundColor: '#e9ecef',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, #4285f4 0%, #34a853 25%, #fbbc05 50%, #ea4335 75%, #4285f4 100%)',
          animation: 'loading 1.2s ease-in-out infinite'
        }} />
      </div>
    </div>
  );
};

/**
 * Simple Loading Bar Component
 * Just the animated horizontal bar for lists
 */
const LoadingBar = ({ width = '100%', height = '4px' }) => {
  return (
    <div style={{
      width: width,
      height: height,
      backgroundColor: '#e9ecef',
      borderRadius: '2px',
      overflow: 'hidden',
      margin: '20px 0'
    }}>
      <div style={{
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, #4285f4 0%, #34a853 25%, #fbbc05 50%, #ea4335 75%, #4285f4 100%)',
        animation: 'loading 1.2s ease-in-out infinite'
      }} />
    </div>
  );
};

/**
 * Custom Confirmation Modal Component
 * Replaces native JavaScript confirm dialogs with styled modal
 */
const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = 'OK', cancelText = 'Cancel', isDangerous = false }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      animation: 'fadeIn 0.2s ease-in-out'
    }}>
      <div style={{
        backgroundColor: '#2b2b2b',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '500px',
        width: '90%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        animation: 'slideIn 0.3s ease-out'
      }}>
        <h2 style={{
          color: '#ffffff',
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '16px',
          marginTop: 0
        }}>
          {title}
        </h2>
        <p style={{
          color: '#e0e0e0',
          fontSize: '16px',
          lineHeight: '1.5',
          marginBottom: '32px',
          marginTop: 0
        }}>
          {message}
        </p>
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              fontWeight: '500',
              backgroundColor: '#4a5568',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minWidth: '100px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5a6578'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#4a5568'}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              fontWeight: '500',
              backgroundColor: isDangerous ? '#dc3545' : '#a8b9ff',
              color: isDangerous ? 'white' : '#1a1a1a',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              minWidth: '100px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = isDangerous ? '#c82333' : '#94a8ff'}
            onMouseOut={(e) => e.target.style.backgroundColor = isDangerous ? '#dc3545' : '#a8b9ff'}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * AdminPanel Component
 * Slide-in panel for user and clinic management
 */
const AdminPanel = ({ isOpen, onClose, currentUser, currentClinic, availableClinics = [] }) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState({});
  const [globalTopics, setGlobalTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingClinic, setEditingClinic] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false); // Deprecated: create invite disabled in UI
  const [showCreateClinic, setShowCreateClinic] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [showCreateInvitation, setShowCreateInvitation] = useState(false); // Deprecated: create invite disabled in UI
  // Topics management state
  const [topicsQuery, setTopicsQuery] = useState('');
  const [topicsResults, setTopicsResults] = useState([]);
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [newTopicFileReference, setNewTopicFileReference] = useState('');
  const [editingTopic, setEditingTopic] = useState(null);

  // Clinic quota state (for non-super-admin users)
  const [clinicQuota, setClinicQuota] = useState(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'OK',
    cancelText: 'Cancel',
    isDangerous: false
  });

  // Clinics search state
  const [clinicsQuery, setClinicsQuery] = useState('');
  const [clinicsResults, setClinicsResults] = useState([]);

  // Users search state
  const [usersQuery, setUsersQuery] = useState('');
  const [usersResults, setUsersResults] = useState([]);
  
  // Ref to store search timeout ID for debouncing
  const searchTimeoutRef = useRef(null);

  // Handle mount/unmount with closing animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      setIsClosing(true);
      const t = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen, shouldRender]);

  // User permissions (memoized to prevent effect loops)
  const permissions = useMemo(() => {
    return permissionManager.getAvailableActions(currentUser);
  }, [currentUser]);

  // Define load functions with useCallback to prevent re-renders
  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      let allUsers = [];

      if (permissions.isSuperAdmin) {
        // Super admins: load all users, then attach raw clinic memberships (names resolved at render time)
        const baseUsers = await userManager.listUsers();
        const enriched = await Promise.all((baseUsers || []).map(async (u) => {
          try {
            if (!u.cognitoSub) return u;
            const mc = await multiClinicUserManager.getUserMultiClinicData(u.cognitoSub);
            if (mc && mc.clinics) {
              return { ...u, clinicMemberships: mc.clinics };
            }
          } catch (_) {}
          return u;
        }));
        allUsers = enriched;
      } else if (permissions.isClinicAdmin) {
        // Clinic admins: gather users across all clinics where this user is admin/super_admin
        const adminClinicIds = (currentUser?.clinicAssociations || currentUser?.clinicMemberships || [])
          .filter(c => (c.role === 'admin' || c.role === 'super_admin'))
          .map(c => c.clinicId);

        const targetClinicIds = adminClinicIds.length > 0 ? adminClinicIds : [currentUser.clinicId];

        // Fetch users per admin clinic, and track clinic membership for display
        const perClinicResults = await Promise.all(targetClinicIds.map(id => userManager.getUsersByClinic(id)));

        // Helper: Get unique identifier from user object (handles both Cognito and DynamoDB records)
        const getUserKey = (u) => u.username || u.email || u.name_scan || u.userId;

        const membershipMap = new Map(); // user key -> Set of clinicIds
        perClinicResults.forEach((list, idx) => {
          const cid = targetClinicIds[idx];
          (list || []).forEach(u => {
            const key = getUserKey(u);
            if (!membershipMap.has(key)) membershipMap.set(key, new Set());
            membershipMap.get(key).add(cid);
          });
        });

        // Flatten users
        allUsers = perClinicResults.flat();

        // Annotate users with clinics (for display only) and ensure email populated (from name_scan if missing)
        allUsers = allUsers.map(u => {
          const key = getUserKey(u);
          const set = membershipMap.get(key);
          const annotated = set ? { ...u, adminClinicsForDisplay: Array.from(set) } : u;
          if (!annotated.email && annotated.name_scan) {
            annotated.email = annotated.name_scan;
          }
          return annotated;
        });
      }

      // Remove duplicates by userId (Cognito sub), falling back to username if not available
      const uniqueUsers = allUsers.filter((user, index, self) =>
        index === self.findIndex(u => {
          const uid1 = u.userId || u.username;
          const uid2 = user.userId || user.username;
          return uid1 && uid2 && uid1 === uid2;
        })
      );

      const filteredUsers = permissionManager.filterUsers(currentUser, uniqueUsers);
      setUsers(filteredUsers);
      setUsersResults(filteredUsers); // Initialize search results
    } catch (error) {
      console.error('Error loading users:', error);
      throw error;
    } finally {
      setUsersLoading(false);
    }
  }, [permissions, currentUser]);

  const loadClinics = useCallback(async () => {
    try {
      setClinicsLoading(true);
      if (permissions.canManageClinics) {
        // Super admin: load all clinics
        const clinicConfigs = await clinicManager.listClinics();
        const clinicsData = clinicConfigs.clinics || {};
        setClinics(clinicsData);
        setClinicsResults(Object.entries(clinicsData).map(([id, clinic]) => ({ ...clinic, id }))); // Initialize search results
      } else if (permissions.canManageInvites) {
        // Clinic admin: derive clinics from availableClinics (names included), restricted to admin/super_admin roles
        const adminSet = new Set(
          (currentUser?.clinicAssociations || currentUser?.clinicMemberships || [])
            .filter(c => c.role === 'admin' || c.role === 'super_admin')
            .map(c => c.clinicId)
        );

        const adminClinics = (availableClinics || []).filter(c => adminSet.has(c.id));
        const map = Object.fromEntries(
          adminClinics.map(c => [c.id, { name: c.name || c.id, topics: c.topics || [], patientRequestPlaceholder: c.patientRequestPlaceholder || '' }])
        );
        setClinics(map);
        setClinicsResults(Object.entries(map).map(([id, clinic]) => ({ ...clinic, id })));
      } else {
        // No management permissions; clear clinics
        setClinics({});
        setClinicsResults([]);
      }
    } catch (error) {
      console.error('Error loading clinics:', error);
      throw error;
    } finally {
      setClinicsLoading(false);
    }
  }, [permissions, currentUser, availableClinics]);

  const loadGlobalTopics = useCallback(async () => {
    try {
      setTopicsLoading(true);
      // Get all available topics from the topic manager instead of hardcoded list
      const topicsResult = await topicManager.searchTopics('', 100); // Reasonable limit to get topics
      const activeTopics = topicsResult.filter(t => t.status !== 'inactive');
      setGlobalTopics(activeTopics);
    } catch (error) {
      console.error('Error loading global topics:', error);
      console.error('Error details:', error.message, error.stack);
      // Don't throw - just set empty array and show error in UI
      setGlobalTopics([]);
      setError(`Failed to load topics: ${error.message}`);
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  const loadInvitations = useCallback(async () => {
    try {
      setInvitationsLoading(true);
      if (permissions.isSuperAdmin) {
        // Super admins: list across all clinics
        const resp = await inviteAPI.listInvites(undefined);
        const flat = (resp?.invites || []).map(invite => ({
          ...invite,
          status: invite.status || 'pending',
          createdAt: invite.createdAt || new Date().toISOString(),
          expiresAt: invite.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        }));
        // Group by email
        const grouped = Array.from(flat.reduce((map, inv) => {
          const key = (inv.email || '').toLowerCase();
          if (!map.has(key)) map.set(key, { email: inv.email, invites: [] });
          map.get(key).invites.push(inv);
          return map;
        }, new Map()).values());
        setInvitations(grouped);
      } else if (permissions.canManageInvites) {
        // Clinic admins: aggregate invites from all clinics where user is admin/super_admin
        const associations = currentUser?.clinicAssociations || currentUser?.clinicMemberships || [];
        const adminClinicIds = Array.from(new Set(
          associations
            .filter(c => c.role === 'admin' || c.role === 'super_admin')
            .map(c => c.clinicId)
        ));

        if (adminClinicIds.length === 0) {
          setInvitations([]);
        } else {
          const results = await Promise.all(adminClinicIds.map(id => inviteAPI.listInvites(id)));
          const merged = results.flatMap(r => (r?.invites || []));
          const flat = merged.map(invite => ({
            ...invite,
            status: invite.status || 'pending',
            createdAt: invite.createdAt || new Date().toISOString(),
            expiresAt: invite.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
          }));
          // Group by email
          const grouped = Array.from(flat.reduce((map, inv) => {
            const key = (inv.email || '').toLowerCase();
            if (!map.has(key)) map.set(key, { email: inv.email, invites: [] });
            map.get(key).invites.push(inv);
            return map;
          }, new Map()).values());
          setInvitations(grouped);
        }
      } else {
        setInvitations([]);
      }
    } catch (error) {
      console.error('Error loading invitations:', error);
      // Show user-friendly fallback
      setInvitations([]);
      // Don't throw - just log the error and continue with empty state
    } finally {
      setInvitationsLoading(false);
    }
  }, [permissions, currentUser]);

  // Load clinic quota for non-super-admin users
  const loadClinicQuota = useCallback(async () => {
    // Only load for non-super-admin users who have a clinic
    if (permissions.isSuperAdmin || !currentClinic) {
      setClinicQuota(null);
      return;
    }
    try {
      const result = await quotaManager.getClinicQuotaStatus(currentClinic?.id);
      if (result.success) {
        setClinicQuota(result);
      }
    } catch (error) {
      console.error('Error loading clinic quota:', error);
      // Don't fail silently - just log and continue
    }
  }, [permissions.isSuperAdmin, currentClinic]);

  // Main data loading function that calls all sub-loaders
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load all data in parallel, but don't fail if topics fail to load
      const results = await Promise.allSettled([
        loadUsers(),
        loadClinics(),
        loadGlobalTopics(),
        loadInvitations(),
        loadClinicQuota()
      ]);
      
      // Check if any critical operations failed (users/clinics/invitations)
      const criticalFailures = results.slice(0, 2).concat([results[3]]).filter(r => r.status === 'rejected');
      if (criticalFailures.length > 0) {
        setError(`Failed to load: ${criticalFailures.map(f => f.reason?.message || 'Unknown error').join(', ')}`);
      }
      
      // If topics failed to load, just warn but don't block the UI
      if (results[2].status === 'rejected') {
        console.warn('Topics failed to load:', results[2].reason);
        setGlobalTopics([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loadClinics, loadInvitations, loadUsers, loadGlobalTopics, loadClinicQuota]);

  // Topic search handler with debouncing
  const handleSearchTopics = useCallback((q) => {
    setTopicsQuery(q);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // If query is empty, load all topics
    if (!q || q.trim().length === 0) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          setTopicsLoading(true);
          const results = await topicManager.listTopics(100); // Load first 100 topics
          setTopicsResults(results);
        } catch (err) {
          console.error('Error loading all topics:', err);
          setError(err.message);
        } finally {
          setTopicsLoading(false);
        }
      }, 100);
      return;
    }
    
    // Debounce search requests - wait 300ms after user stops typing
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setTopicsLoading(true);
        const results = await topicManager.searchTopics(q.trim(), 100);
        setTopicsResults(results);
      } catch (err) {
        console.error('Error searching topics:', err);
        setError(err.message);
      } finally {
        setTopicsLoading(false);
      }
    }, 300);
  }, []);

  // Clinic search handler with debouncing
  const handleSearchClinics = useCallback((q) => {
    setClinicsQuery(q);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If query is empty, load all clinics from current clinics state
    if (!q || q.trim().length === 0) {
      setClinicsResults(Object.entries(clinics).map(([id, clinic]) => ({ ...clinic, id })));
      return;
    }

    // Debounce search requests - wait 300ms after user stops typing
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await clinicManager.searchClinics(q.trim(), 100);
        setClinicsResults(results);
      } catch (err) {
        console.error('Error searching clinics:', err);
        setError(err.message);
      }
    }, 300);
  }, [clinics]);

  // User search handler with debouncing
  const handleSearchUsers = useCallback((q) => {
    setUsersQuery(q);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If query is empty, load all users from current users state
    if (!q || q.trim().length === 0) {
      setUsersResults(users);
      return;
    }

    // Debounce search requests - wait 300ms after user stops typing
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await userManager.searchUsers(q.trim(), 100);
        setUsersResults(results);
      } catch (err) {
        console.error('Error searching users:', err);
        setError(err.message);
      }
    }, 300);
  }, [users]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleCreateTopic = useCallback(async () => {
    if (!newTopicName.trim()) return;
    if (!newTopicFileReference.trim()) {
      setError('File reference is required');
      return;
    }
    try {
      setCreatingTopic(true);
      await topicManager.createTopic(
        newTopicName.trim(), 
        newTopicFileReference.trim()
      );
      setNewTopicName('');
      setNewTopicFileReference('');
      // Refresh the topics list
      if (topicsQuery) {
        const results = await topicManager.searchTopics(topicsQuery, 100);
        setTopicsResults(results);
      } else {
        const results = await topicManager.listTopics(100);
        setTopicsResults(results);
      }
      await loadGlobalTopics();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingTopic(false);
    }
  }, [newTopicName, newTopicFileReference, topicsQuery, loadGlobalTopics]);

  const handleToggleTopicStatus = useCallback(async (id, currentStatus) => {
    try {
      const next = currentStatus === 'active' ? 'inactive' : 'active';
      await topicManager.setTopicStatus(id, next);
      if (topicsQuery) {
        const results = await topicManager.searchTopics(topicsQuery, 50);
        setTopicsResults(results);
      }
      await loadGlobalTopics();
    } catch (err) {
      setError(err.message);
    }
  }, [topicsQuery, loadGlobalTopics]);

  const handleEditTopic = useCallback((topic) => {
    setEditingTopic(topic);
  }, []);

  const handleUpdateTopic = useCallback(async (id, name, fileReference) => {
    try {
      setLoading(true);
      await topicManager.updateTopic(id, name, fileReference);
      // Refresh the topics list
      if (topicsQuery) {
        const results = await topicManager.searchTopics(topicsQuery, 100);
        setTopicsResults(results);
      } else {
        const results = await topicManager.listTopics(100);
        setTopicsResults(results);
      }
      await loadGlobalTopics();
      setEditingTopic(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [topicsQuery, loadGlobalTopics]);

  const handleDeleteTopic = useCallback((id) => {
    const topic = topicsResults.find(t => t.id === id);
    const topicName = topic?.name || id;

    setConfirmModal({
      isOpen: true,
      title: 'Delete Topic',
      message: `Are you sure you want to permanently delete "${topicName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          await topicManager.deleteTopic(id);
          if (topicsQuery) {
            const results = await topicManager.searchTopics(topicsQuery, 100);
            setTopicsResults(results);
          } else {
            const results = await topicManager.listTopics(100);
            setTopicsResults(results);
          }
          await loadGlobalTopics();
        } catch (err) {
          setError(err.message);
        }
      }
    });
  }, [topicsQuery, topicsResults, loadGlobalTopics]);

  // Load data when panel opens
  useEffect(() => {
    if (isOpen && currentUser) {
      loadData();
    }
  }, [isOpen, currentUser, loadData]);

  // Auto-load topics when Topics tab becomes active
  useEffect(() => {
    if (activeTab === 'topics' && permissions.isSuperAdmin && !topicsQuery) {
      // Trigger list to load all topics
      handleSearchTopics('');
    }
  }, [activeTab, permissions.isSuperAdmin, topicsQuery, handleSearchTopics]);

  // Auto-load topics when Clinics tab becomes active (needed for topic selection)
  useEffect(() => {
    if (activeTab === 'clinics' && permissions.canManageClinics && globalTopics.length === 0) {
      loadGlobalTopics().catch(error => {
        console.warn('Failed to load topics for clinics tab, but continuing anyway:', error);
      });
    }
  }, [activeTab, permissions.canManageClinics, globalTopics.length, loadGlobalTopics]);

  const handleUpdateUser = async (username, updates) => {
    try {
      setLoading(true);
      await userManager.updateUser(username, updates);
      await loadUsers();
      setEditingUser(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (identifier) => {
    // Find the target user by any matching identifier (username, userId/cognitoSub, or email)
    const targetUser = users.find(u =>
      (u.username && u.username === identifier) ||
      (u.userId && u.userId === identifier) ||
      (u.cognitoSub && u.cognitoSub === identifier) ||
      (u.email && u.email === identifier)
    );
    if (!targetUser) {
      setError('User not found');
      return;
    }

    // Check if trying to delete self
    if ((currentUser.username && currentUser.username === targetUser.username) ||
        (currentUser.email && currentUser.email === targetUser.email) ||
        (currentUser.cognitoSub && (currentUser.cognitoSub === targetUser.cognitoSub || currentUser.cognitoSub === targetUser.userId))) {
      setError('You cannot delete your own account');
      return;
    }

    // Check permissions
    if (!permissionManager.canDeleteUser(currentUser, targetUser)) {
      setError('You do not have permission to delete this user');
      return;
    }

    // Show custom confirmation modal with user email
    setConfirmModal({
      isOpen: true,
      title: 'BrainQ Care Management Portal',
      message: `Are you sure you want to delete user ${targetUser.email || identifier}?`,
      confirmText: 'OK',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: async () => {
        // Close modal
        setConfirmModal(prev => ({ ...prev, isOpen: false }));

        // Perform deletion — pass username or userId (Cognito AdminGetUserCommand accepts both)
        try {
          setLoading(true);
          const deleteId = targetUser.username || targetUser.userId || identifier;
          await userManager.deleteUser(deleteId);
          await loadUsers();
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Multi-clinic editor modal state
  const [editMembershipUser, setEditMembershipUser] = useState(null);
  const [editMembershipData, setEditMembershipData] = useState(null);
  const [editMembershipLoading, setEditMembershipLoading] = useState(false);

  const openMembershipEditor = async (user) => {
    // Check if trying to edit self
    if ((currentUser.username && currentUser.username === user.username) ||
        (currentUser.email && currentUser.email === user.email) ||
        (currentUser.cognitoSub && (currentUser.cognitoSub === user.cognitoSub || currentUser.cognitoSub === user.userId))) {
      setError('You cannot edit your own account');
      return;
    }

    // Check permissions
    if (!permissionManager.canManageUsers(currentUser, user)) {
      setError('You do not have permission to edit this user');
      return;
    }

    setEditMembershipUser(user);
    setEditMembershipLoading(true);
    try {
      // Pass the user's Cognito sub explicitly to get their multi-clinic data
      // DynamoDB records store this as userId, Cognito records as cognitoSub
      const userSub = user.cognitoSub || user.userId || user.sub;

      if (!userSub) {
        throw new Error('User Cognito sub not found');
      }

      const data = await multiClinicUserManager.getUserMultiClinicData(userSub);
      setEditMembershipData(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setEditMembershipLoading(false);
    }
  };

  const closeMembershipEditor = () => {
    setEditMembershipUser(null);
    setEditMembershipData(null);
  };

  const handleUpdateClinic = async (clinicId, updates) => {
    try {
      setLoading(true);
      await clinicManager.createOrUpdateClinic(clinicId, updates);
      await loadClinics();
      setEditingClinic(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClinic = async (clinicIdOrData, maybeClinicData) => {
    try {
      setLoading(true);

      // Support new flow where server generates clinicId
      if (typeof clinicIdOrData === 'object') {
        await clinicManager.createOrUpdateClinic(null, clinicIdOrData);
      } else {
        await clinicManager.createOrUpdateClinic(clinicIdOrData, maybeClinicData);
      }
      await loadClinics();
      setShowCreateClinic(false);
    } catch (error) {
      console.error('Clinic creation failed:', error);
      setError(`Failed to create clinic: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClinic = async (clinicId) => {
    if (!clinicId) {
      setError('Cannot delete clinic: missing clinic ID');
      return;
    }

    const clinicName = clinics[clinicId]?.name || clinicId;

    setConfirmModal({
      isOpen: true,
      title: 'Delete Clinic',
      message: `Are you sure you want to delete clinic "${clinicName}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          setLoading(true);
          await clinicManager.deleteClinic(clinicId);
          await loadClinics();
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleSetSuperAdmin = (username) => {
    setConfirmModal({
      isOpen: true,
      title: 'Set Super Admin',
      message: `Are you sure you want to make "${username}" a super admin? This will grant full system access.`,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          setLoading(true);
          await userManager.setSuperAdmin(username);
          await loadUsers();
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleCreateInvitation = async (invitationData) => {
    try {
      setLoading(true);
      setError(null);
      const clinicsToInvite = Array.isArray(invitationData.clinics) ? invitationData.clinics : [];
      if (clinicsToInvite.length === 0) {
        throw new Error('Please select at least one clinic');
      }
      for (const c of clinicsToInvite) {
        if (!c.topics || !Array.isArray(c.topics) || c.topics.length === 0) {
          throw new Error(`Please select at least one topic for clinic ${clinics[c.clinicId]?.name || c.clinicId}`);
        }
        const payload = {
          email: invitationData.email,
          clinicId: c.clinicId,
          role: c.role || 'viewer', // Use per-clinic role
          topics: c.topics
        };
        const response = await inviteAPI.createInvite(payload);
        if (!response.success) {
          throw new Error(response.error || `Failed to invite for clinic ${c.clinicId}`);
        }
      }

      // Reload invitations and users to get the latest data
      await loadInvitations();
      await loadUsers();
      setShowCreateInvitation(false);
      setShowCreateUser(false);
    } catch (error) {
      console.error('Error creating invitation:', error);
      // Check if this is a "user already exists" error - show friendly message
      if (/user already exists/i.test(error.message)) {
        setError(`This email (${invitationData.email}) is already registered. To add an existing user to additional clinics, please use the "Edit User" option in User Management.`);
      } else {
        setError(`Failed to create invitation: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeInvitation = (inviteId) => {
    const invitation = invitations.find(inv => inv.inviteId === inviteId);
    const email = invitation?.email || 'this user';

    setConfirmModal({
      isOpen: true,
      title: 'Delete Invitation',
      message: `Are you sure you want to delete the invitation for "${email}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDangerous: true,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          setLoading(true);
          setError(null);

          const response = await inviteAPI.revokeInvite(inviteId);

          if (response.success) {
            await loadInvitations();
          } else {
            throw new Error(response.error || 'Failed to revoke invitation');
          }
        } catch (error) {
          console.error('Error revoking invitation:', error);
          setError(`Failed to revoke invitation: ${error.message}`);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleResendInvitation = async (inviteId) => {
    const invitation = invitations.find(inv => inv.inviteId === inviteId);

    try {
      setLoading(true);
      setError(null);

      const response = await inviteAPI.resendInvite(inviteId);

      if (response.success) {
        const email = response.email || invitation?.email || 'the recipient';
        alert(`Invitation resent to ${email}`);
        // Reload invitations to get any updated timestamps
        await loadInvitations();
      } else {
        throw new Error(response.error || 'Failed to resend invitation');
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError(`Failed to resend invitation: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!shouldRender) return null;

  const overlayClass = `admin-panel-overlay ${isOpen && !isClosing ? 'animate-in' : ''} ${isClosing ? 'animate-out' : ''}`;
  const panelClass = `admin-panel ${isOpen && !isClosing ? 'animate-in' : ''} ${isClosing ? 'animate-out' : ''}`;

  return (
    <div className={overlayClass} onMouseDown={e => {
      // Only close if clicking directly on overlay AND no modal is open inside
      if (e.target === e.currentTarget) {
        const hasOpenModal = document.querySelector('.prf-overlay, .modal-overlay');
        if (!hasOpenModal) onClose();
      }
    }}>
      <div className={panelClass} onClick={e => e.stopPropagation()}>
        <div className="admin-panel-header">
          <h2>Management</h2>
          <button className="close-button" onClick={onClose} aria-label="Close admin panel">
            ×
          </button>
        </div>

        {error && (
          <div className="error-message" role="alert">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Clinic Quota Banner for non-super-admin users */}
        {clinicQuota && !permissions.isSuperAdmin && (
          <div className="clinic-quota-banner">
            <div className="quota-banner-content">
              <div className="quota-banner-label">
                <span className="quota-clinic-name">{clinicQuota.clinicName}</span>
                <span className="quota-title">Credits</span>
              </div>
              <div className="quota-banner-stats">
                <span className="quota-numbers">
                  <span className="quota-current">{clinicQuota.patientMonths ?? 0}</span>
                  <span className="quota-separator">/</span>
                  <span className="quota-limit">{clinicQuota.quota?.limit ?? '∞'}</span>
                </span>
                {clinicQuota.quotaStatus !== 'no_quota' && (
                  <div className={`quota-status-badge quota-status-${clinicQuota.quotaStatus}`}>
                    {clinicQuota.quotaStatus === 'ok' && 'OK'}
                    {clinicQuota.quotaStatus === 'warning' && `${clinicQuota.remaining ?? 0} left`}
                    {clinicQuota.quotaStatus === 'exceeded' && 'Exceeded'}
                  </div>
                )}
              </div>
            </div>
            {clinicQuota.quotaStatus !== 'no_quota' && (
              <div className="quota-progress-bar">
                <div
                  className={`quota-progress-fill quota-fill-${clinicQuota.quotaStatus}`}
                  style={{ width: `${Math.min(clinicQuota.utilizationPercent, 100)}%` }}
                />
              </div>
            )}
          </div>
        )}

        <div className="admin-panel-tabs">
          <button
            className={`tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            disabled={!permissions.canViewUsersList}
          >
            Users
          </button>
          {permissions.canManageInvites && (
            <button
              className={`tab ${activeTab === 'invitations' ? 'active' : ''}`}
              onClick={() => setActiveTab('invitations')}
            >
              Invitations
            </button>
          )}
          {permissions.canManageClinics && (
            <button
              className={`tab ${activeTab === 'clinics' ? 'active' : ''}`}
              onClick={() => setActiveTab('clinics')}
            >
              Clinics
            </button>
          )}
          {permissions.isSuperAdmin && (
            <button
              className={`tab ${activeTab === 'topics' ? 'active' : ''}`}
              onClick={() => setActiveTab('topics')}
            >
              Topics
            </button>
          )}
          {permissions.isSuperAdmin && (
            <button
              className={`tab ${activeTab === 'quotas' ? 'active' : ''}`}
              onClick={() => setActiveTab('quotas')}
            >
              Quotas
            </button>
          )}
          {permissions.canManageUsers && (
            <button
              className={`tab ${activeTab === 'patient-requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('patient-requests')}
            >
              Patient Requests
            </button>
          )}
        </div>

        <div className="admin-panel-content">
          {loading && <LoadingBar />}

          {activeTab === 'users' && (
            <UsersTab
              users={users}
              clinics={clinics}
              globalTopics={globalTopics}
              permissions={permissions}
              currentUser={currentUser}
              availableClinics={availableClinics}
              editingUser={editingUser}
              loading={usersLoading}
              onEditUser={setEditingUser}
              onUpdateUser={handleUpdateUser}
              onDeleteUser={handleDeleteUser}
              openMembershipEditor={openMembershipEditor}
              editMembershipUser={editMembershipUser}
              editMembershipData={editMembershipData}
              editMembershipLoading={editMembershipLoading}
              closeMembershipEditor={closeMembershipEditor}
              loadUsers={loadUsers}
              usersQuery={usersQuery}
              usersResults={usersResults}
              onSearchUsers={handleSearchUsers}
            />
          )}

          {activeTab === 'clinics' && permissions.canManageClinics && (
            <ClinicsTab
              clinics={clinics}
              globalTopics={globalTopics}
              editingClinic={editingClinic}
              showCreateClinic={showCreateClinic}
              loading={clinicsLoading}
              onEditClinic={setEditingClinic}
              onUpdateClinic={handleUpdateClinic}
              onCreateClinic={handleCreateClinic}
              onDeleteClinic={handleDeleteClinic}
              onShowCreateClinic={setShowCreateClinic}
              clinicsQuery={clinicsQuery}
              clinicsResults={clinicsResults}
              onSearchClinics={handleSearchClinics}
            />
          )}

          {activeTab === 'invitations' && permissions.canManageInvites && (
            <InvitationsTab
              invitations={invitations}
              users={users}
              clinics={clinics}
              globalTopics={globalTopics}
              permissions={permissions}
              currentUser={currentUser}
              currentClinic={currentClinic}
              availableClinics={availableClinics}
              showCreateInvitation={showCreateInvitation}
              loading={invitationsLoading}
              onCreateInvitation={handleCreateInvitation}
              onShowCreateInvitation={setShowCreateInvitation}
              onResendInvitation={handleResendInvitation}
            />
          )}

          {activeTab === 'topics' && permissions.isSuperAdmin && (
            <TopicsTab
              query={topicsQuery}
              results={topicsResults}
              onSearch={handleSearchTopics}
              newName={newTopicName}
              setNewName={setNewTopicName}
              newFileReference={newTopicFileReference}
              setNewFileReference={setNewTopicFileReference}
              onCreate={handleCreateTopic}
              creating={creatingTopic}
              loading={topicsLoading}
              editingTopic={editingTopic}
              onEdit={handleEditTopic}
              onUpdate={handleUpdateTopic}
              onCancelEdit={() => setEditingTopic(null)}
              onDelete={handleDeleteTopic}
            />
          )}

          {activeTab === 'quotas' && permissions.isSuperAdmin && (
            <QuotasTab clinics={clinics} />
          )}

          {activeTab === 'patient-requests' && permissions.canManageUsers && (
            <PatientRequestsTab
              clinics={clinics}
              currentClinic={currentClinic}
              permissions={permissions}
              currentUser={currentUser}
            />
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        isDangerous={confirmModal.isDangerous}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

/**
 * Multi-clinic membership editor modal
 */
const MultiClinicEditor = ({ user, data, clinicsMap, globalTopics, currentUser, loading = false, onClose, onChanged }) => {
  const [memberships, setMemberships] = useState([]);
  const [adding, setAdding] = useState({ clinicId: '', role: 'viewer', topics: [] });
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // data is expected to be the payload returned from getUserMultiClinicData (already unwrapped)
    const allowedClinicIds = Object.keys(clinicsMap || {});
    if (data?.clinics && Array.isArray(data.clinics)) {
      const filtered = allowedClinicIds.length > 0
        ? data.clinics.filter(m => allowedClinicIds.includes(m.clinicId))
        : data.clinics;
      setMemberships(filtered);
      return;
    }
    // Fallbacks for older shapes
    if (data?.userData?.clinics && Array.isArray(data.userData.clinics)) {
      const filtered = allowedClinicIds.length > 0
        ? data.userData.clinics.filter(m => allowedClinicIds.includes(m.clinicId))
        : data.userData.clinics;
      setMemberships(filtered);
      return;
    }
  }, [data, clinicsMap]);

  const saveRole = async (clinicId, role) => {
    setBusy(true);
    setError(null);
    try {
      await multiClinicUserManager.updateUserRoleInClinic(
        user.cognitoSub || user.userId || user.sub,
        clinicId,
        role,
        currentUser?.sub || currentUser?.cognitoSub
      );
      onChanged && onChanged();
      setMemberships(prev => prev.map(m => m.clinicId === clinicId ? { ...m, role } : m));
    } catch (err) {
      console.error('Update role error:', err);
      setError(err.message || 'Failed to update role');
    } finally {
      setBusy(false);
    }
  };

  const saveTopics = async (clinicId, topics) => {
    setBusy(true);
    setError(null);
    try {
      // Use the multi-clinic API to update user topics for clinic
      await multiClinicUserManager.updateUserTopicsForClinic(
        user.cognitoSub || user.userId || user.sub,
        clinicId,
        topics,
        currentUser?.sub || currentUser?.cognitoSub
      );
      onChanged && onChanged();
      setMemberships(prev => prev.map(m => m.clinicId === clinicId ? { ...m, topics } : m));
    } catch (err) {
      console.error('Update topics error:', err);
      setError(err.message || 'Failed to update topics');
    } finally {
      setBusy(false);
    }
  };

  const removeClinic = (clinicId) => {
    const clinicName = clinicsMap?.[clinicId]?.name || clinicId;
    setConfirmRemove({ clinicId, clinicName });
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemove) return;
    const { clinicId } = confirmRemove;
    setConfirmRemove(null);
    setBusy(true);
    setError(null);

    // Save old state for rollback
    const previousMemberships = memberships;

    try {
      await multiClinicUserManager.removeUserFromClinic(
        user.cognitoSub || user.userId || user.sub,
        clinicId,
        currentUser?.sub || currentUser?.cognitoSub
      );
      // Only update local state after successful API call (use functional update to avoid stale closure)
      setMemberships(prev => prev.filter(m => m.clinicId !== clinicId));
      onChanged && onChanged();
    } catch (err) {
      console.error('Remove clinic error:', err);
      setError(err.message || 'Failed to remove clinic membership');
    } finally {
      setBusy(false);
    }
  };

  const addClinic = async () => {
    if (!adding.clinicId) return;
    setBusy(true);
    setError(null);
    try {
      // Get the admin Cognito sub from currentUser
      const adminSub = currentUser?.cognitoSub || currentUser?.sub;

      if (!adminSub) {
        throw new Error('Admin user authentication is required');
      }

      // Get target user's Cognito sub
      const targetUserSub = user.cognitoSub || user.sub;

      if (!targetUserSub) {
        throw new Error('Target user Cognito sub not found');
      }

      // Use the multi-clinic API to add user to clinic
      await multiClinicUserManager.addUserToClinic(
        targetUserSub,
        adding.clinicId,
        adding.role,
        adding.topics,
        adminSub
      );
      onChanged && onChanged();
      const newMembership = { clinicId: adding.clinicId, role: adding.role, topics: adding.topics };
      setMemberships(prev => [...prev, newMembership]);
      setAdding({ clinicId: '', role: 'viewer', topics: [] });
    } catch (err) {
      console.error('Add clinic error:', err);
      setError(err.message || 'Failed to add clinic membership');
    } finally {
      setBusy(false);
    }
  };

  const availableClinics = Object.keys(clinicsMap || {}).filter(id => !memberships.some(m => m.clinicId === id));

  // Helper function to get topic name from ID
  const getTopicName = (topicId) => {
    const topic = globalTopics?.find(t => t.id === topicId);
    return topic?.name || topicId;
  };

  return (
    <>
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Edit Memberships for {user.email || user.username}</h3>
          <button onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading && <LoadingBar />}

          {error && (
            <div style={{ color: '#dc3545', marginBottom: 16, padding: '8px 12px', backgroundColor: '#fef2f2', borderRadius: 4 }}>
              {error}
            </div>
          )}

          {availableClinics.length > 0 && (
            <div className="add-membership-section" style={{ marginBottom: 16 }}>
              <h4 className="add-membership-title">Add New Clinic Membership</h4>
              <div className="add-membership-form">
                <div className="add-membership-row">
                  <select
                    value={adding.clinicId}
                    onChange={e => {
                      const clinicId = e.target.value;
                      const clinicTopics = clinicsMap[clinicId]?.topics || [];
                      setAdding(a => ({ ...a, clinicId: clinicId, topics: clinicTopics }));
                    }}
                  >
                    <option value="">Select clinic</option>
                    {availableClinics.map(id => (
                      <option key={id} value={id}>{clinicsMap[id]?.name || id}</option>
                    ))}
                  </select>
                  <select
                    value={adding.role}
                    onChange={e => setAdding(a => ({ ...a, role: e.target.value }))}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {adding.clinicId && (clinicsMap[adding.clinicId]?.topics || []).length > 0 && (
                  <div>
                    <label className="membership-topics-label">Select Topics:</label>
                    <div className="membership-topics-grid">
                      {(clinicsMap[adding.clinicId]?.topics || []).map(t => (
                        <label key={`add-${t}`} className="membership-topic-item">
                          <input
                            type="checkbox"
                            checked={adding.topics.includes(t)}
                            onChange={e => setAdding(a => ({
                              ...a,
                              topics: e.target.checked
                                ? [...new Set([...a.topics, t])]
                                : a.topics.filter(x => x!==t)
                            }))}
                          />
                          {getTopicName(t)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="add-membership-actions">
                  <button
                    className="btn-add"
                    onClick={addClinic}
                    disabled={busy || !adding.clinicId}
                  >
                    {busy ? 'Adding...' : 'Add Membership'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {(!loading && memberships.length === 0) ? (
            <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px', padding: '20px 0' }}>
              No clinic memberships yet.
            </div>
          ) : (
            memberships.map(m => (
              <div key={m.clinicId} className="membership-card">
                <div className="membership-card-header">
                  <h4 className="membership-clinic-name">
                    {clinicsMap[m.clinicId]?.name || m.clinicId}
                  </h4>
                  <div className="membership-actions">
                    <select
                      value={m.role}
                      onChange={e => saveRole(m.clinicId, e.target.value)}
                      disabled={busy}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                    <button
                      className="btn-danger"
                      onClick={() => removeClinic(m.clinicId)}
                      disabled={busy}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                {(clinicsMap[m.clinicId]?.topics || []).length > 0 && (
                  <div className="membership-card-body">
                    <label className="membership-topics-label">Available Topics:</label>
                    <div className="membership-topics-grid">
                      {(clinicsMap[m.clinicId]?.topics || []).map(t => (
                        <label key={`${m.clinicId}-${t}`} className="membership-topic-item">
                          <input
                            type="checkbox"
                            checked={m.topics?.includes(t) || false}
                            onChange={e => {
                              const next = e.target.checked
                                ? [...new Set([...(m.topics||[]), t])]
                                : (m.topics||[]).filter(x => x!==t);
                              saveTopics(m.clinicId, next);
                            }}
                            disabled={busy}
                          />
                          {getTopicName(t)}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}

          {/* Add membership form rendered above for better visibility */}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} disabled={busy}>Close</button>
        </div>
      </div>
    </div>

    <ConfirmationModal
      isOpen={!!confirmRemove}
      title="Remove Clinic Membership"
      message={`Are you sure you want to remove this user from "${confirmRemove?.clinicName}"?`}
      confirmText="Remove"
      cancelText="Cancel"
      isDangerous={true}
      onConfirm={handleConfirmRemove}
      onCancel={() => setConfirmRemove(null)}
    />
  </>
  );
};

/**
 * Users management tab
 */
const UsersTab = ({
  users, clinics, globalTopics, permissions, currentUser, availableClinics,
  editingUser, loading, onEditUser, onUpdateUser,
  onDeleteUser,
  openMembershipEditor, editMembershipUser, editMembershipData, editMembershipLoading, closeMembershipEditor,
  loadUsers, usersQuery, usersResults, onSearchUsers
}) => {
  return (
    <div className="users-tab">
      <div className="tab-header">
        <h3>Users Management</h3>
        {/* Invite creation disabled in UI */}
      </div>

      <div className="search-bar" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={usersQuery}
          onChange={(e) => onSearchUsers(e.target.value)}
          className="edit-input"
          style={{ flex: 1 }}
        />
      </div>

      {/* Invite creation form hidden */}

      <div className="users-list">
        {loading && <LoadingBar />}

        {!loading && usersResults.map(user => {
          // Use a reliable identifier: DynamoDB records have userId, Cognito records have username
          const uid = user.username || user.userId || user.cognitoSub || user.email;
          return (
          <UserCard
            key={uid}
            user={user}
            clinics={clinics}
            globalTopics={globalTopics}
            permissions={permissions}
            currentUser={currentUser}
            availableClinics={availableClinics}
            isEditing={editingUser === uid}
            onEdit={() => onEditUser(uid)}
            onEditMemberships={() => openMembershipEditor(user)}
            onUpdate={(updates) => onUpdateUser(uid, updates)}
            onDelete={() => onDeleteUser(uid)}
            onCancelEdit={() => onEditUser(null)}
          />
          );
        })}

        {!loading && (!usersResults || usersResults.length === 0) && (
          <div className="empty-state">No users found</div>
        )}
      </div>

      {editMembershipUser && (
        <MultiClinicEditor
          user={editMembershipUser}
          data={editMembershipData}
          clinicsMap={clinics}
          globalTopics={globalTopics}
          currentUser={currentUser}
          loading={editMembershipLoading}
          onClose={closeMembershipEditor}
          onChanged={async () => {
            try {
              await loadUsers();  // Refresh user list after membership changes
              await onEditUser(null);
            } catch (_) {}
          }}
        />
      )}
    </div>
  );
};

/**
 * Clinics management tab
 */
const ClinicsTab = ({
  clinics, globalTopics, editingClinic, showCreateClinic, loading,
  onEditClinic, onUpdateClinic, onCreateClinic, onDeleteClinic, onShowCreateClinic,
  clinicsQuery, clinicsResults, onSearchClinics
}) => {
  const [newClinicName, setNewClinicName] = useState('');
  const [newClinicTopics, setNewClinicTopics] = useState([]);
  const [creatingClinic, setCreatingClinic] = useState(false);

  const handleCreateClinic = async () => {
    if (!newClinicName.trim()) return;
    try {
      setCreatingClinic(true);
      await onCreateClinic({ name: newClinicName.trim(), topics: newClinicTopics });
      setNewClinicName('');
      setNewClinicTopics([]);
      onShowCreateClinic(false);
    } catch (error) {
      console.error('Failed to create clinic:', error);
    } finally {
      setCreatingClinic(false);
    }
  };

  const handleTopicToggle = (topicId, checked) => {
    setNewClinicTopics(prev =>
      checked
        ? [...prev, topicId]
        : prev.filter(id => id !== topicId)
    );
  };

  return (
    <div className="clinics-tab">
      <div className="tab-header">
        <h3>Clinics Management</h3>
        <button
          className="create-button"
          style={{
            backgroundColor: showCreateClinic ? '#6c757d' : '#28a745',
            color: 'white'
          }}
          onClick={() => {
            onShowCreateClinic(!showCreateClinic);
            if (showCreateClinic) {
              setNewClinicName('');
              setNewClinicTopics([]);
            }
          }}
        >
          {showCreateClinic ? 'Cancel' : 'Add Clinic'}
        </button>
      </div>

      <div className="search-bar" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Search clinics by name..."
          value={clinicsQuery}
          onChange={(e) => onSearchClinics(e.target.value)}
          className="edit-input"
          style={{ flex: 1 }}
        />
      </div>

      {showCreateClinic && (
        <div className="create-form" style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <h4 style={{ marginTop: 0 }}>Create New Clinic</h4>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Clinic name"
              value={newClinicName}
              onChange={(e) => setNewClinicName(e.target.value)}
              style={{ flex: 1 }}
              required
            />
          </div>
          {globalTopics.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: '500', fontSize: '14px' }}>Available Topics:</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {globalTopics.map(topic => (
                  <label key={topic.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newClinicTopics.includes(topic.id)}
                      onChange={(e) => handleTopicToggle(topic.id, e.target.checked)}
                    />
                    {topic.name || topic.id}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleCreateClinic}
              disabled={creatingClinic || !newClinicName.trim()}
              style={{
                backgroundColor: creatingClinic || !newClinicName.trim() ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: creatingClinic || !newClinicName.trim() ? 'not-allowed' : 'pointer'
              }}
            >
              {creatingClinic ? 'Creating...' : 'Create Clinic'}
            </button>
            <button
              onClick={() => {
                onShowCreateClinic(false);
                setNewClinicName('');
                setNewClinicTopics([]);
              }}
              type="button"
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="clinics-list">
        {loading && <LoadingBar />}

        {!loading && clinicsResults.map((clinic) => (
          <ClinicCard
            key={clinic.id}
            clinicId={clinic.id}
            clinic={clinic}
            globalTopics={globalTopics}
            isEditing={editingClinic === clinic.id}
            onEdit={() => onEditClinic(clinic.id)}
            onUpdate={(updates) => onUpdateClinic(clinic.id, updates)}
            onDelete={() => onDeleteClinic(clinic.id)}
            onCancelEdit={() => onEditClinic(null)}
          />
        ))}

        {!loading && (!clinicsResults || clinicsResults.length === 0) && (
          <div className="empty-state">No clinics found</div>
        )}
      </div>
    </div>
  );
};

/**
 * Topics management tab (super admins)
 */
const TopicsTab = ({
  query, results, onSearch,
  newName, setNewName, newFileReference, setNewFileReference,
  onCreate, creating, loading, editingTopic, onEdit, onUpdate, onCancelEdit, onDelete
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editFormData, setEditFormData] = useState({ name: '', fileReference: '' });

  useEffect(() => {
    if (editingTopic) {
      setEditFormData({
        name: editingTopic.name || '',
        fileReference: editingTopic.fileReference || ''
      });
    }
  }, [editingTopic]);

  const handleCreate = async () => {
    await onCreate();
    // Hide form after successful creation
    setShowAddForm(false);
  };

  const handleUpdate = async () => {
    if (editingTopic) {
      await onUpdate(editingTopic.id, editFormData.name, editFormData.fileReference);
    }
  };

  return (
    <div className="topics-tab">
      <div className="tab-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3>Topics Management</h3>
        <button
          className="add-button"
          style={{ 
            backgroundColor: '#007cba', 
            color: 'white', 
            border: 'none', 
            padding: '8px 16px', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : 'Add Topic'}
        </button>
      </div>

      <div className="search-bar" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Search topics by name..."
          value={query}
          onChange={(e) => onSearch(e.target.value)}
          className="edit-input"
          style={{ flex: 1 }}
        />
      </div>

      {showAddForm && (
        <div className="create-form" style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <h4 style={{ marginTop: 0 }}>Create New Topic</h4>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Topic name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{ flex: 1 }}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="File reference (S3 ARN or HTTP URL) - required"
              value={newFileReference}
              onChange={(e) => setNewFileReference(e.target.value)}
              style={{ flex: 1 }}
              required
            />
            <button onClick={handleCreate} disabled={creating || !newName.trim() || !newFileReference.trim()}>
              {creating ? 'Creating...' : 'Create Topic'}
            </button>
            <button onClick={() => setShowAddForm(false)} type="button">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="topics-list">
        {loading && <LoadingBar />}

        {!loading && results.map(t => {
          const isEditing = editingTopic && editingTopic.id === t.id;

          if (isEditing) {
            return (
              <div key={`${t.id}|${t.name_scan}`} className="topic-card editing">
                <div className="topic-edit-content">
                  <div className="topic-edit-header">
                    <h4 className="edit-title">Edit Topic</h4>
                  </div>
                  <div className="topic-edit-form">
                    <div className="edit-field">
                      <input
                        type="text"
                        placeholder="Topic name"
                        value={editFormData.name}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="edit-input"
                        required
                      />
                    </div>
                    <div className="edit-field">
                      <input
                        type="text"
                        placeholder="File reference (S3 ARN or HTTP URL)"
                        value={editFormData.fileReference}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, fileReference: e.target.value }))}
                        className="edit-input"
                        required
                      />
                    </div>
                    <div className="edit-actions">
                      <button
                        className="btn-primary"
                        onClick={handleUpdate}
                        disabled={!editFormData.name.trim() || !editFormData.fileReference.trim()}
                      >
                        Save
                      </button>
                      <button className="btn-secondary" onClick={onCancelEdit} type="button">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={`${t.id}|${t.name_scan}`} className="topic-card">
              <div className="topic-content">
                <div className="topic-left">
                  <h4 className="topic-name">{t.name || t.id}</h4>
                </div>
                <div className="topic-right">
                  <button className="btn-secondary" onClick={() => onEdit(t)}>Edit</button>
                  <button className="btn-danger" onClick={() => onDelete(t.id)}>Delete</button>
                </div>
              </div>
            </div>
          );
        })}

        {!loading && (!results || results.length === 0) && (
          <div className="empty-state">No topics found</div>
        )}
      </div>
    </div>
  );
};

/**
 * Individual user card component
 */
const UserCard = ({
  user, clinics, globalTopics, permissions, currentUser, availableClinics = [],
  isEditing, onEdit, onUpdate, onDelete, onCancelEdit,
  onEditMemberships
}) => {
  const [formData, setFormData] = useState({
    firstName: user.firstName || '',
    clinicId: user.clinicId || '',
    role: user.role || 'viewer',
    topics: user.topics || []
  });

  // Check if this is the current user's own card
  const isOwnCard = currentUser && (
    (currentUser.username && currentUser.username === user.username) ||
    (currentUser.email && currentUser.email === user.email) ||
    (currentUser.cognitoSub && (currentUser.cognitoSub === user.cognitoSub || currentUser.cognitoSub === user.userId))
  );

  const canEdit = permissions.canEditUsers && 
                 permissionManager.canManageUsers(currentUser, user);
  const canDelete = permissions.canDeleteUsers && 
                   permissionManager.canDeleteUser(currentUser, user);

  // Super admins can select from all topics, others only from clinic topics
  const availableTopics = permissions.isSuperAdmin ? 
    globalTopics : 
    globalTopics.filter(topic => clinics[formData.clinicId]?.topics?.includes(topic.id)) || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  const handleTopicChange = (topicId, checked) => {
    setFormData(prev => ({
      ...prev,
      topics: checked 
        ? [...prev.topics, topicId]
        : prev.topics.filter(t => t !== topicId)
    }));
  };

  const handleSave = () => {
    if (!formData.firstName.trim()) return;
    const updates = { firstName: formData.firstName.trim() };
    onUpdate(updates);
  };

  if (isEditing) {
    return (
      <div className="card user-card editing">
        <div className="card-edit-content user-edit-content">
          <div className="card-edit-header user-edit-header">
            <h4 className="card-edit-title edit-title">Edit User</h4>
          </div>
          <div className="card-edit-form user-edit-form">
            <div className="card-edit-field edit-field">
              <input
                type="text"
                placeholder="First name"
                value={formData.firstName}
                onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="card-edit-input edit-input"
                required
              />
            </div>

            <div className="card-edit-actions edit-actions">
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={!formData.firstName.trim()}
              >
                Save
              </button>
              <button className="btn-secondary" onClick={onCancelEdit} type="button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for display
  const getDisplayName = () => {
    if (user.firstName) {
      return `${user.firstName} (${user.email})`;
    }
    return user.email;
  };

  // Determine viewer's admin clinics; null means all clinics (super admin)
  const getViewerAdminClinicIds = () => {
    if (permissions.isSuperAdmin) return null;
    let ids = [];
    if (Array.isArray(currentUser?.clinicMemberships)) {
      ids = currentUser.clinicMemberships
        .filter(m => m.role === 'admin' || m.role === 'super_admin')
        .map(m => m.clinicId);
    } else if (Array.isArray(currentUser?.clinicAssociations)) {
      ids = currentUser.clinicAssociations
        .filter(m => m.role === 'admin' || m.role === 'super_admin')
        .map(m => m.clinicId);
    }
    return Array.from(new Set(ids));
  };

  // Get ALL clinics viewer is a member of (any role) - used for display visibility
  const getViewerMemberClinicIds = () => {
    if (permissions.isSuperAdmin) return null; // Super admin can see all
    let ids = [];
    if (Array.isArray(currentUser?.clinicMemberships)) {
      ids = currentUser.clinicMemberships.map(m => m.clinicId);
    } else if (Array.isArray(currentUser?.clinicAssociations)) {
      ids = currentUser.clinicAssociations.map(m => m.clinicId);
    }
    return Array.from(new Set(ids));
  };

  // Build target user's clinics array in unified shape
  const getTargetClinics = () => {
    if (Array.isArray(user.clinicMemberships) && user.clinicMemberships.length > 0) {
      return user.clinicMemberships.map(m => ({ clinicId: m.clinicId, topics: m.topics || [] }));
    }
    if (Array.isArray(user.clinics) && user.clinics.length > 0) {
      return user.clinics.map(m => ({ clinicId: m.clinicId, topics: m.topics || [] }));
    }
    if (user.clinicId) return [{ clinicId: user.clinicId, topics: user.topics || [] }];
    return [];
  };

  const getClinicsList = () => {
    if (!user) return 'None assigned';
    const resolveClinicName = (cid) => {
      // Try multiple sources for clinic name
      // 1. From loaded clinics (admin view)
      if (clinics[cid]?.name) return clinics[cid].name;

      // 2. From availableClinics prop
      const fromAvailable = availableClinics.find(c => c.id === cid);
      if (fromAvailable?.name && fromAvailable.name !== cid) return fromAvailable.name;

      // 3. From currentUser's clinic memberships (they should have the name)
      if (currentUser?.clinicMemberships) {
        const fromMemberships = currentUser.clinicMemberships.find(c => c.clinicId === cid);
        if (fromMemberships?.clinicName && fromMemberships.clinicName !== cid) return fromMemberships.clinicName;
      }

      // 4. From currentUser's clinic associations (legacy)
      if (currentUser?.clinicAssociations) {
        const fromAssociations = currentUser.clinicAssociations.find(c => c.clinicId === cid);
        if (fromAssociations?.clinicName && fromAssociations.clinicName !== cid) return fromAssociations.clinicName;
      }

      // 5. Fallback to ID (this means we couldn't find the name anywhere)
      return cid;
    };

    const target = getTargetClinics();
    if (target.length === 0) return 'None assigned';

    // Determine which clinics we can show (all clinics viewer is a member of)
    const viewerMemberIds = getViewerMemberClinicIds();
    const allowedTarget = viewerMemberIds === null ? target : target.filter(c => viewerMemberIds.includes(c.clinicId));

    // Build list of visible clinic names (fallback to ID). HiddenCount is clinics you cannot see only.
    const names = allowedTarget.map(c => resolveClinicName(c.clinicId));
    const hiddenCount = target.length - allowedTarget.length;

    const distinct = Array.from(new Set(names));
    const visibleLabel = distinct.join(', ');

    // Format display: show visible names, add (+count) for hidden clinics
    if (hiddenCount > 0) {
      return visibleLabel ? `${visibleLabel} (+${hiddenCount})` : `(+${hiddenCount})`;
    } else {
      return visibleLabel || 'None assigned';
    }
  };

  const getTopicsList = () => {
    if (!user) return 'None assigned';

    const resolveName = (id) => {
      const t = (globalTopics || []).find(gt => gt.id === id);
      return t ? (t.name || t.id) : id;
    };

    const target = getTargetClinics();
    if (target.length === 0) return 'None assigned';

    // Show topics from clinics where viewer is a member (any role)
    const viewerMemberIds = getViewerMemberClinicIds();
    const visible = viewerMemberIds === null ? target : target.filter(c => viewerMemberIds.includes(c.clinicId));
    const hidden = viewerMemberIds === null ? [] : target.filter(c => !viewerMemberIds.includes(c.clinicId));

    // Visible topics (distinct by ID)
    const norm = (x) => String(x ?? '').trim().toLowerCase();
    const visibleTopicIds = Array.from(new Set(visible.flatMap(m => m.topics || []).map(norm)));
    const visibleNames = Array.from(new Set(visibleTopicIds.map(resolveName)));
    const visibleLabel = visibleNames.join(', ');

    // Hidden topics that are not already covered by visible topics
    const hiddenTopicIds = new Set(hidden.flatMap(m => (m.topics || []).map(norm)));
    const extraHiddenCount = Array.from(hiddenTopicIds).filter(id => !visibleTopicIds.includes(id)).length;

    // Format display: show visible names, add (+count) for hidden topics
    if (extraHiddenCount > 0) {
      return visibleLabel ? `${visibleLabel} (+${extraHiddenCount})` : `(+${extraHiddenCount})`;
    } else {
      return visibleLabel || 'None assigned';
    }
  };

  return (
    <div className="card user-card">
      <div className="card-content user-content">
        <div className="card-left user-left">
          <h4 className="card-title user-name">{getDisplayName()}</h4>
          {/* Email shown in title; avoid redundant line */}
          <div className="card-subtitle user-info-line user-clinics">Clinics: {getClinicsList()}</div>
          <div className="card-subtitle user-info-line user-topics">Topics: {getTopicsList()}</div>
        </div>
        {!isOwnCard && (canEdit || canDelete) && (
          <div className="card-actions user-right">
            {canEdit && <button
              className="btn-secondary"
              onClick={onEditMemberships}
              title="Edit user memberships"
            >
              Edit
            </button>}
            {canDelete && <button
              className="btn-danger"
              onClick={onDelete}
              title="Delete user"
            >
              Delete
            </button>}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Create user form component
 */
const CreateUserForm = ({
  clinics, globalTopics, permissions, currentUser,
  onSubmit, onCancel
}) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    clinicId: permissions.isSuperAdmin ? '' : currentUser.clinicId,
    role: 'viewer',
    topics: []
  });

  // Super admins can select from all topics, others only from clinic topics
  const availableTopics = permissions.isSuperAdmin ? 
    globalTopics : 
    globalTopics.filter(topic => clinics[formData.clinicId]?.topics?.includes(topic.id)) || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleTopicChange = (topicId, checked) => {
    setFormData(prev => ({
      ...prev,
      topics: checked 
        ? [...prev.topics, topicId]
        : prev.topics.filter(t => t !== topicId)
    }));
  };

  return (
    <div className="create-form">
      <h4>Create New User</h4>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
            required
          />
        </div>

        <div className="form-group">
          <label>First Name</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
            required
          />
        </div>

        <div className="form-group">
          <label>Clinic</label>
          <select
            value={formData.clinicId}
            onChange={e => setFormData(prev => ({ ...prev, clinicId: e.target.value }))}
            required
            disabled={!permissions.isSuperAdmin}
          >
            <option value="">Select Clinic</option>
            {Object.entries(clinics).map(([id, clinic]) => (
              <option key={id} value={id}>{clinic.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Role</label>
          <select
            value={formData.role}
            onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
            required
          >
            <option value="viewer">Viewer</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className="form-group">
          <label>Topics</label>
          <div className="topics-checkboxes">
            {availableTopics.map(topic => (
              <label key={topic.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.topics.includes(topic.id)}
                  onChange={e => handleTopicChange(topic.id, e.target.checked)}
                />
                {topic.name || topic.id}
              </label>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit">Create User</button>
          <button type="button" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

/**
 * Individual clinic card component
 */
const ClinicCard = ({
  clinicId, clinic, globalTopics, isEditing,
  onEdit, onUpdate, onDelete, onCancelEdit
}) => {
  const [formData, setFormData] = useState({
    name: clinic.name || '',
    topics: clinic.topics || [],
    patientRequestPlaceholder: clinic.patientRequestPlaceholder || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
  };

  const handleTopicChange = (topicId, checked) => {
    setFormData(prev => ({
      ...prev,
      topics: checked 
        ? [...prev.topics, topicId]
        : prev.topics.filter(t => t !== topicId)
    }));
  };

  if (isEditing) {
    return (
      <div className="clinic-card editing">
        <div className="clinic-edit-content">
          <div className="clinic-edit-header">
            <h4 className="edit-title">Edit Clinic</h4>
          </div>
          <form onSubmit={handleSubmit} className="clinic-edit-form">
            <div className="edit-field">
              <label className="edit-label">Clinic Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="edit-input"
                required
              />
            </div>

            <div className="edit-field">
              <label className="edit-label">Available Topics</label>
              {globalTopics.length === 0 ? (
                <p className="no-topics-message">
                  No topics available. Create topics first.
                </p>
              ) : (
                <div className="topics-checkboxes">
                  {globalTopics.map(topic => (
                    <label key={topic.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.topics.includes(topic.id)}
                        onChange={e => handleTopicChange(topic.id, e.target.checked)}
                      />
                      {topic.name || topic.id}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="edit-field">
              <label className="edit-label">Patient Request - Clinic Field Placeholder</label>
              <input
                type="text"
                value={formData.patientRequestPlaceholder}
                onChange={e => setFormData(prev => ({ ...prev, patientRequestPlaceholder: e.target.value }))}
                className="edit-input"
                placeholder="e.g., Site Name, Location, Department..."
              />
              <span className="field-hint" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                Placeholder text shown in the Clinic field on patient request forms
              </span>
            </div>

            <div className="edit-actions">
              <button type="submit" className="btn-primary">Save</button>
              <button type="button" className="btn-secondary" onClick={onCancelEdit}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Helper function to get topic names for display
  const getTopicNames = (topicIds) => {
    if (!topicIds || !Array.isArray(topicIds)) return 'None';
    return topicIds.map(id => {
      const topic = globalTopics.find(t => t.id === id);
      return topic ? (topic.name || topic.id) : id;
    }).join(', ');
  };

  return (
    <div className="clinic-card">
      <div className="clinic-content">
        <div className="clinic-left">
          <h4 className="clinic-name">{clinic.name}</h4>
          <div className="clinic-topics">{getTopicNames(clinic.topics)}</div>
        </div>

        <div className="clinic-right">
          <button className="btn-secondary" onClick={onEdit}>Edit</button>
          <button className="btn-danger" onClick={onDelete}>Delete</button>
        </div>
      </div>
    </div>
  );
};

// CreateClinicForm component removed - now inline in ClinicsTab

/**
 * Invitations management tab
 */
const InvitationsTab = ({
  invitations,
  users,
  clinics,
  globalTopics,
  permissions,
  currentUser,
  currentClinic,
  availableClinics,
  showCreateInvitation,
  loading,
  onCreateInvitation,
  onShowCreateInvitation,
  onResendInvitation
}) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (invitation) => {
    const status = invitation.status;
    let className = 'status-badge';
    let text = status;

    if (status === 'expired') {
      className += ' expired';
      text = 'expired';
    } else if (status === 'pending') {
      className += ' pending';
      text = 'pending';
    } else if (status === 'accepted') {
      className += ' accepted';
      text = 'accepted';
    } else if (status === 'revoked') {
      className += ' revoked';
      text = 'revoked';
    }

    return <span className={className}>{text}</span>;
  };

  return (
    <div className="invitations-tab">
      <div className="tab-header">
        <h3>Invitations Management</h3>
        <button
          className="create-button"
          onClick={() => onShowCreateInvitation(true)}
          disabled={loading || showCreateInvitation}
        >
          Invite Users
        </button>
      </div>

      {showCreateInvitation && (
        <CreateInvitationForm
          clinics={clinics}
          globalTopics={globalTopics}
          currentClinic={currentClinic}
          availableClinics={availableClinics}
          onSubmit={onCreateInvitation}
          onCancel={() => onShowCreateInvitation(false)}
          loading={loading}
        />
      )}

      <div className="invitations-list">
        {loading && <LoadingBar />}

        {!loading && invitations.length === 0 && (
          <div className="empty-state">
            <p>No invitations found</p>
            <button
              className="primary-button"
              onClick={() => onShowCreateInvitation(true)}
              disabled={showCreateInvitation}
            >
              Send First Invitation
            </button>
          </div>
        )}

        {!loading && invitations.length > 0 && (
          <div className="table-responsive">
            <table className="invitations-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Clinics</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(group => {
                  const clinicNames = Array.from(new Set((group.invites || []).map(inv => (
                    (clinics && clinics[inv.clinicId]?.name)
                      || (Array.isArray(availableClinics) && (availableClinics.find(c => c.id === inv.clinicId)?.name))
                      || inv.clinicName
                      || inv.clinicId
                  ))));
                  // Determine primary status with priority
                  // IMPORTANT: "accepted" only shows if user exists in system
                  const groupStatus = (() => {
                    const invites = group.invites || [];

                    // Check if user exists in the system
                    const userExists = users.some(u =>
                      u.email?.toLowerCase() === group.email?.toLowerCase()
                    );

                    // Check for accepted (highest priority) - BUT only if user exists
                    const hasAccepted = invites.some(inv => inv.status === 'accepted');
                    if (hasAccepted && userExists) {
                      return 'accepted';
                    }

                    // Check for active pending (not expired)
                    const hasActivePending = invites.some(inv => {
                      if (inv.status !== 'pending') return false;
                      if (!inv.expiresAt) return true; // No expiration = still pending
                      return new Date(inv.expiresAt) >= new Date(); // Not expired yet
                    });
                    if (hasActivePending) {
                      return 'pending';
                    }

                    // Check for expired (pending that passed expiration)
                    const hasExpired = invites.some(inv => {
                      if (inv.status !== 'pending') return false;
                      return inv.expiresAt && new Date(inv.expiresAt) < new Date();
                    });
                    if (hasExpired) {
                      return 'expired';
                    }

                    // If user doesn't exist but had accepted invites, show revoked
                    // Otherwise default to first invite's status
                    if (hasAccepted && !userExists) {
                      return 'revoked';
                    }

                    return invites[0]?.status || 'revoked';
                  })();

                  // Find a resendable invitation (pending or expired)
                  const resendableInvite = (group.invites || []).find(inv => {
                    if (inv.status === 'pending') {
                      const isExpired = inv.expiresAt && new Date(inv.expiresAt) < new Date();
                      return true; // Can resend both active pending and expired
                    }
                    return false;
                  });
                  const canResend = resendableInvite && (groupStatus === 'pending' || groupStatus === 'expired');
                  const resendInviteId = canResend ? resendableInvite.inviteId : null;

                  return (
                    <tr key={group.email}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{group.email}</div>
                      </td>
                      <td>{clinicNames.join(', ')}</td>
                      <td>{getStatusBadge({ status: groupStatus, expiresAt: resendableInvite?.expiresAt })}</td>
                      <td>
                        <div className="action-buttons">
                          {canResend && (
                            <button
                              className="action-button primary"
                              onClick={() => onResendInvitation(resendInviteId)}
                              title="Resend invitation email"
                              disabled={loading}
                            >
                              Resend
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="invitations-summary">
        <div className="summary-stats">
          <div className="stat">
            <span className="stat-number">{invitations.length}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat">
            <span className="stat-number">{invitations.filter(inv => inv.status === 'pending').length}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat">
            <span className="stat-number">{invitations.filter(inv => inv.status === 'accepted').length}</span>
            <span className="stat-label">Accepted</span>
          </div>
          <div className="stat">
            <span className="stat-number">{invitations.filter(inv => inv.status === 'revoked').length}</span>
            <span className="stat-label">Revoked</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Create invitation form - Redesigned for multi-email support
 */
const CreateInvitationForm = ({ clinics, globalTopics, currentClinic, availableClinics = [], onSubmit, onCancel, loading = false }) => {
  const initialSelectedClinics = useMemo(() => {
    // Do not preselect any clinic; require explicit admin choice
    return {};
  }, []);

  const [formData, setFormData] = useState({
    emails: '',  // Changed from single 'email' to multi 'emails'
    selectedClinics: initialSelectedClinics
  });

  // Email parsing helper - extracts valid emails from comma/newline separated string
  const parseEmails = (emailsString) => {
    return emailsString
      .split(/[,\n]/)
      .map(e => e.trim())
      .filter(e => e.length > 0);
  };

  const emailList = parseEmails(formData.emails);
  const emailCount = emailList.length;

  const handleSubmit = (e) => {
    e.preventDefault();
    const clinicsList = Object.entries(formData.selectedClinics)
      .filter(([_, v]) => Array.isArray(v.topics) && v.topics.length > 0)
      .map(([clinicId, v]) => ({ clinicId, topics: v.topics, role: v.role || 'viewer' }));

    const emailList = parseEmails(formData.emails);

    // Send invitation for each email
    emailList.forEach(email => {
      onSubmit({ email, clinics: clinicsList });
    });
  };

  const toggleClinic = (clinicId, checked) => {
    setFormData(prev => {
      const next = { ...prev };
      if (checked) {
        next.selectedClinics = {
          ...next.selectedClinics,
          [clinicId]: { topics: [], role: 'viewer' }
        };
      } else {
        const { [clinicId]: _, ...rest } = next.selectedClinics;
        next.selectedClinics = rest;
      }
      return next;
    });
  };

  const handleTopicChange = (clinicId, topic, checked) => {
    setFormData(prev => {
      const sel = prev.selectedClinics[clinicId] || { topics: [], role: 'viewer' };
      const topics = checked
        ? [...new Set([...(sel.topics || []), topic])]
        : (sel.topics || []).filter(t => t !== topic);
      return {
        ...prev,
        selectedClinics: { ...prev.selectedClinics, [clinicId]: { ...sel, topics } }
      };
    });
  };

  const handleRoleChange = (clinicId, role) => {
    setFormData(prev => {
      const sel = prev.selectedClinics[clinicId] || { topics: [], role: 'viewer' };
      return {
        ...prev,
        selectedClinics: { ...prev.selectedClinics, [clinicId]: { ...sel, role } }
      };
    });
  };

  const clinicIds = Object.keys(clinics || {});
  const selectedClinicCount = Object.keys(formData.selectedClinics).length;

  return (
    <div className="create-invitation-form">
      <h4>Invite Users</h4>
      <form onSubmit={handleSubmit}>
        {/* Multi-Email Input */}
        <div className="form-group">
          <label style={{ textAlign: 'left', display: 'block' }}>Email Addresses</label>
          <textarea
            value={formData.emails}
            onChange={e => setFormData(prev => ({ ...prev, emails: e.target.value }))}
            placeholder="Enter one or more email addresses (comma or newline separated)"
            rows={5}
            required
          />
          <small style={{ display: 'block', marginTop: '4px', color: '#666', textAlign: 'left' }}>
            {emailCount > 0 ? `${emailCount} email(s) entered` : 'Enter one or more email addresses (comma or newline separated)'}
          </small>
        </div>

        {/* Multi-Clinic Selection */}
        <div className="form-group">
          <label style={{ textAlign: 'left', display: 'block' }}>Clinics</label>
          {clinicIds.length === 0 && <div className="empty-state">No clinics available</div>}
          <div style={{
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            padding: '12px',
            backgroundColor: '#fafafa',
            marginBottom: '8px'
          }}>
            <div className="topics-checkboxes">
              {clinicIds.map(cid => (
                <label key={cid} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!!formData.selectedClinics[cid]}
                    onChange={e => toggleClinic(cid, e.target.checked)}
                  />
                  {clinics[cid]?.name || cid}
                </label>
              ))}
            </div>
          </div>
          <small style={{ display: 'block', color: '#666', textAlign: 'left' }}>
            {selectedClinicCount > 0
              ? `${selectedClinicCount} clinic(s) selected - select at least one`
              : 'Select one or more clinics to invite users to'}
          </small>
        </div>

        {/* Role and Topic Selection per Selected Clinic */}
        {clinicIds.filter(cid => formData.selectedClinics[cid]).map(cid => {
          // Get available topics for this clinic - if clinic has topics, use those IDs to find the topic objects
          let availableTopics = globalTopics;
          if (clinics[cid]?.topics && clinics[cid].topics.length > 0) {
            availableTopics = globalTopics.filter(topic => clinics[cid].topics.includes(topic.id));
          }

          const clinicName = clinics[cid]?.name || (availableClinics.find(c => c.id === cid)?.name) || cid;

          return (
            <div key={`clinic-${cid}`} style={{
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#fafafa'
            }}>
              <h5 style={{ marginTop: 0, marginBottom: '12px', color: '#333', textAlign: 'left' }}>{clinicName}</h5>

              {/* Role Selection for this clinic */}
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ textAlign: 'left', display: 'block' }}>Role for {clinicName}</label>
                <select
                  value={formData.selectedClinics[cid]?.role || 'viewer'}
                  onChange={e => handleRoleChange(cid, e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              {/* Topics for this clinic */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ textAlign: 'left', display: 'block' }}>Topics for {clinicName}</label>
                <div className="topics-checkboxes">
                  {availableTopics.map(topic => (
                    <label key={`${cid}-${topic.id}`} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.selectedClinics[cid]?.topics?.includes(topic.id) || false}
                        onChange={e => handleTopicChange(cid, topic.id, e.target.checked)}
                      />
                      {topic.name || topic.id}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        {/* Submit + Cancel Buttons */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={selectedClinicCount === 0 || emailCount === 0 || loading}
          >
            {loading
              ? 'Sending...'
              : emailCount > 1 ? `Send ${emailCount} Invitations` : emailCount === 1 ? 'Send Invitation' : 'Send Invitation(s)'}
          </button>
          <button type="button" onClick={onCancel} disabled={loading}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

/**
 * Quotas management tab (super admin only)
 */
const QuotasTab = ({ clinics }) => {
  const [quotaStatuses, setQuotaStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingClinic, setEditingClinic] = useState(null);
  const [editForm, setEditForm] = useState({ limit: '', emailList: '', organizationalUnit: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadQuotaStatuses();
  }, []);

  const loadQuotaStatuses = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await quotaManager.getAllQuotaStatuses();
      setQuotaStatuses(result.clinics || []);
    } catch (err) {
      console.error('Error loading quota statuses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (clinic) => {
    setEditingClinic(clinic.clinicId);
    setEditForm({
      limit: clinic.quota?.limit ?? '',
      emailList: clinic.emailNotificationList || '',
      organizationalUnit: clinic.organizationalUnit || ''
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const clinicId = editingClinic;

      // Update quota if limit is provided
      if (editForm.limit !== '') {
        await quotaManager.setClinicQuota(clinicId, {
          limit: parseInt(editForm.limit, 10)
        });
      }

      // Update email list
      await quotaManager.setClinicEmailNotificationList(clinicId, editForm.emailList);

      // Update organizationalUnit through clinic manager if changed
      const originalClinic = quotaStatuses.find(c => c.clinicId === clinicId);
      if (editForm.organizationalUnit !== (originalClinic?.organizationalUnit || '')) {
        await clinicManager.createOrUpdateClinic(clinicId, {
          name: originalClinic.clinicName,
          organizationalUnit: editForm.organizationalUnit
        });
      }

      setEditingClinic(null);
      loadQuotaStatuses();
    } catch (err) {
      console.error('Error saving quota:', err);
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingIndicator message="Loading quota data..." />;
  }

  if (error) {
    return (
      <div className="error-message">
        Error loading quotas: {error}
        <button onClick={loadQuotaStatuses} className="btn-secondary" style={{ marginLeft: '10px' }}>
          Retry
        </button>
      </div>
    );
  }

  // Get status color scheme
  const getStatusColors = (status) => {
    switch (status) {
      case 'ok':
        return { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46', badge: '#10b981' };
      case 'warning':
        return { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', badge: '#f59e0b' };
      case 'exceeded':
        return { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', badge: '#ef4444' };
      default:
        return { bg: '#f9fafb', border: '#e5e7eb', text: '#6b7280', badge: '#9ca3af' };
    }
  };

  return (
    <div className="quotas-tab">
      <div className="tab-header">
        <h3>Patient Quotas</h3>
        <button onClick={loadQuotaStatuses} className="btn-icon" title="Refresh">
          <RefreshIcon size={18} />
        </button>
      </div>

      <div className="quotas-grid">
        {quotaStatuses.map(clinic => {
          const colors = getStatusColors(clinic.quotaStatus);
          const hasQuota = clinic.quotaStatus !== 'no_quota' && clinic.quota?.limit != null;

          return (
            <div key={clinic.clinicId} className={`quota-card-v2 ${editingClinic === clinic.clinicId ? 'editing' : ''}`}>
              {editingClinic === clinic.clinicId ? (
                <div className="quota-edit-v2">
                  <div className="quota-edit-header">
                    <h4>{clinic.clinicName}</h4>
                  </div>
                  <div className="quota-edit-fields">
                    <div className="quota-field">
                      <label>Patient Limit</label>
                      <input
                        type="number"
                        value={editForm.limit}
                        onChange={e => setEditForm(prev => ({ ...prev, limit: e.target.value }))}
                        placeholder="e.g., 50"
                        min="0"
                      />
                      <span className="field-hint">Leave empty for no quota</span>
                    </div>
                    <div className="quota-field">
                      <label>Organizational Unit</label>
                      <input
                        type="text"
                        value={editForm.organizationalUnit}
                        onChange={e => setEditForm(prev => ({ ...prev, organizationalUnit: e.target.value }))}
                        placeholder="e.g., BrainQ.INDOMA"
                      />
                      <span className="field-hint">Used for patient care documents</span>
                    </div>
                    <div className="quota-field">
                      <label>Notification Emails</label>
                      <input
                        type="text"
                        value={editForm.emailList}
                        onChange={e => setEditForm(prev => ({ ...prev, emailList: e.target.value }))}
                        placeholder="admin@clinic.com"
                      />
                      <span className="field-hint">Comma-separated for multiple</span>
                    </div>
                  </div>
                  <div className="quota-edit-actions">
                    <button onClick={() => setEditingClinic(null)} className="btn-cancel" disabled={saving}>
                      Cancel
                    </button>
                    <button onClick={handleSave} className="btn-save" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="quota-card-header">
                    <div className="quota-clinic-info">
                      <h4 className="quota-clinic-title">{clinic.clinicName}</h4>
                      {clinic.organizationalUnit && (
                        <span className="quota-ou-badge">{clinic.organizationalUnit}</span>
                      )}
                    </div>
                    <button onClick={() => handleEdit(clinic)} className="btn-edit">
                      Edit
                    </button>
                  </div>

                  <div className="quota-card-body">
                    {hasQuota ? (
                      <>
                        <div className="quota-visual">
                          <div className="quota-circle" style={{ borderColor: colors.badge }}>
                            <span className="quota-number">{clinic.patientMonths ?? 0}</span>
                            <span className="quota-label">Credits</span>
                          </div>
                          <div className="quota-details">
                            <div className="quota-main-stat">
                              <span className="quota-active">{clinic.patientMonths ?? 0}</span>
                              <span className="quota-divider">/</span>
                              <span className="quota-total">{clinic.quota.limit}</span>
                            </div>
                            <div
                              className="quota-status-pill"
                              style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
                            >
                              {clinic.quotaStatus === 'ok' && `${clinic.remaining ?? 0} available`}
                              {clinic.quotaStatus === 'warning' && `${clinic.remaining ?? 0} remaining`}
                              {clinic.quotaStatus === 'exceeded' && 'Quota exceeded'}
                            </div>
                            <div className="quota-progress-track">
                              <div
                                className="quota-progress-bar-fill"
                                style={{
                                  width: `${Math.min(clinic.utilizationPercent, 100)}%`,
                                  backgroundColor: colors.badge
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="quota-no-limit">
                        <div className="no-limit-icon">∞</div>
                        <div className="no-limit-text">
                          <span className="no-limit-title">No quota set</span>
                          <span className="no-limit-subtitle">Unlimited Credits</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {clinic.emailNotificationList && (
                    <div className="quota-card-footer">
                      <span className="quota-email-label">Notifications:</span>
                      <div className="quota-email-chips">
                        {clinic.emailNotificationList.split(',').map((email, idx) => (
                          <span key={idx} className="quota-email-chip">{email.trim()}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}

        {quotaStatuses.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <div className="empty-text">No clinics found</div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Patient Requests management tab
 */
const PatientRequestsTab = ({ clinics, currentClinic, permissions, currentUser }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedClinicForRequest, setSelectedClinicForRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);  // For editing existing requests

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const clinicId = permissions.isSuperAdmin ? '*' : currentClinic?.id;
      const result = await patientRequestManager.listRequests(clinicId, statusFilter || null);
      setRequests(result.requests || []);
    } catch (err) {
      console.error('Error loading patient requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    try {
      setProcessingId(requestId);
      await patientRequestManager.updateStatus(requestId, newStatus);
      loadRequests();
    } catch (err) {
      console.error('Error updating request status:', err);
      alert(`Failed to update status: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const getClinicName = (clinicId) => {
    return clinics[clinicId]?.name || clinicId;
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return 'N/A';
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: '#92400e', bg: '#fef3c7', border: '#fcd34d', label: 'Pending' },
      approved: { color: '#065f46', bg: '#d1fae5', border: '#6ee7b7', label: 'Approved' },
      rejected: { color: '#991b1b', bg: '#fee2e2', border: '#fca5a5', label: 'Rejected' }
    };
    return configs[status] || { color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db', label: status };
  };

  const formatGender = (gender) => {
    if (!gender) return null;
    const found = GENDER_OPTIONS.find(opt => opt.id === gender);
    return found?.name || gender;
  };

  const formatTreatmentType = (treatmentType) => {
    if (!treatmentType) return null;
    const found = getTreatmentTypeById(treatmentType);
    return found?.name || treatmentType;
  };

  // Check if current user can edit a request (requester or admin)
  const canEditRequest = (request) => {
    if (request.status !== 'pending') return false;
    // Check if user is the original requester
    const isRequester = currentUser?.sub === request.requestedBy ||
                        currentUser?.email === request.requestedByEmail;
    // Or if user is an admin
    const isAdmin = permissions.isClinicAdmin || permissions.isSuperAdmin;
    return isRequester || isAdmin;
  };

  // Handle editing a request
  const handleEditRequest = (request) => {
    setEditingRequest(request);
    setSelectedClinicForRequest(request.clinicId);
    setShowRequestForm(true);
  };

  // Handle form close
  const handleFormClose = () => {
    setShowRequestForm(false);
    setSelectedClinicForRequest(null);
    setEditingRequest(null);
  };

  if (loading) {
    return <LoadingIndicator message="Loading patient requests..." />;
  }

  if (error) {
    return (
      <div className="error-message">
        Error loading requests: {error}
        <button onClick={loadRequests} className="btn-secondary" style={{ marginLeft: '10px' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="patient-requests-tab">
      <div className="tab-header">
        <h3>Patient Requests</h3>
        <div className="tab-actions">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={loadRequests} className="btn-icon" title="Refresh">
            <RefreshIcon size={16} />
          </button>
          {permissions.isSuperAdmin ? (
            <select
              className="btn-primary-select"
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  setSelectedClinicForRequest(e.target.value);
                  setShowRequestForm(true);
                }
              }}
            >
              <option value="">+ New Request...</option>
              {Object.entries(clinics).map(([id, clinic]) => (
                <option key={id} value={id}>{clinic.name}</option>
              ))}
            </select>
          ) : (
            <button
              className="btn-primary"
              onClick={() => {
                setSelectedClinicForRequest(currentClinic?.id);
                setShowRequestForm(true);
              }}
            >
              + New Request
            </button>
          )}
        </div>
      </div>

      <div className="requests-grid">
        {requests.map(request => {
          const statusConfig = getStatusConfig(request.status);
          const patientData = request.patientData || {};

          return (
            <div key={request.requestId} className="request-card-v2">
              {/* Card Header */}
              <div className="req-card-header">
                <div className="req-patient-info">
                  <span className="req-patient-id">{patientData.idNumber || 'Unknown ID'}</span>
                  <div className="req-status-row">
                    <span
                      className="req-status-badge"
                      style={{ backgroundColor: statusConfig.bg, color: statusConfig.color, borderColor: statusConfig.border }}
                    >
                      {statusConfig.label}
                    </span>
                    {canEditRequest(request) && (
                      <button
                        onClick={() => handleEditRequest(request)}
                        className="req-btn-edit-icon"
                        disabled={processingId === request.requestId}
                        title="Edit request"
                      >
                        <PenIcon size={12} />
                      </button>
                    )}
                    {request.status === 'approved' && (
                      <button
                        onClick={() => handleEditRequest(request)}
                        className="req-btn-edit-icon"
                        data-testid="patient-request-btn-view"
                        disabled={processingId === request.requestId}
                        title="View request (read only)"
                      >
                        <EyeIcon size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="req-clinic-badge">{getClinicName(request.clinicId)}</div>
              </div>

              {/* Patient Details */}
              <div className="req-card-body">
                <div className="req-details-grid">
                  {formatGender(patientData.gender) && (
                    <div className="req-detail">
                      <span className="req-detail-label">Sex</span>
                      <span className="req-detail-value">{formatGender(patientData.gender)}</span>
                    </div>
                  )}
                  {(patientData.dateOfBirth || patientData.yearOfBirth) && (
                    <div className="req-detail">
                      <span className="req-detail-label">Date of Birth</span>
                      <span className="req-detail-value">{patientData.dateOfBirth || patientData.yearOfBirth}</span>
                    </div>
                  )}
                  {patientData.treatmentType && (
                    <div className="req-detail">
                      <span className="req-detail-label">Injury Type</span>
                      <span className="req-detail-value">{formatTreatmentType(patientData.treatmentType)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Footer */}
              <div className="req-card-footer">
                <div className="req-meta-info">
                  <span className="req-requester">{request.requestedByEmail}</span>
                  <span className="req-date">{formatDate(request.requestedAt)}</span>
                </div>
                <div className="req-actions">
                  {/* Approve/Reject - only for super admin on pending requests */}
                  {request.status === 'pending' && permissions.isSuperAdmin && (
                    <>
                      <button
                        onClick={() => handleUpdateStatus(request.requestId, 'rejected')}
                        className="req-btn-reject"
                        disabled={processingId === request.requestId}
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(request.requestId, 'approved')}
                        className="req-btn-approve"
                        disabled={processingId === request.requestId}
                      >
                        Approve
                      </button>
                    </>
                  )}
                  {/* Reopen - only for super admin on approved requests */}
                  {request.status === 'approved' && permissions.isSuperAdmin && (
                    <button
                      onClick={() => handleUpdateStatus(request.requestId, 'pending')}
                      className="req-btn-reopen"
                      data-testid="patient-request-btn-reopen"
                      disabled={processingId === request.requestId}
                      title="Return this request to pending review"
                    >
                      Reopen
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {requests.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <div className="empty-text">
              {statusFilter ? `No ${statusFilter} requests found` : 'No patient requests yet'}
            </div>
          </div>
        )}
      </div>

      {/* Patient Request Form Modal */}
      <PatientRequestForm
        isOpen={showRequestForm}
        onClose={handleFormClose}
        clinicId={selectedClinicForRequest}
        clinicName={selectedClinicForRequest ? clinics[selectedClinicForRequest]?.name : ''}
        clinicType={clinics[selectedClinicForRequest]?.clinicType}
        clinicPlaceholder={selectedClinicForRequest ? clinics[selectedClinicForRequest]?.patientRequestPlaceholder : ''}
        existingRequest={editingRequest}
        readOnly={!!editingRequest && editingRequest.status !== 'pending'}
        onSuccess={() => {
          loadRequests();
        }}
      />
    </div>
  );
};

export default AdminPanel;
